/**
 * Modello e logica della Ricerca Avanzata, condivisi da Pratiche, Clienti e Fornitori.
 * Ogni schermata descrive i propri campi (AdvancedSearchField); le regole vengono
 * combinate tutte in AND oppure tutte in OR.
 */

export type AdvancedFieldType = 'text' | 'number' | 'date' | 'select';
export type AdvancedMatch = 'and' | 'or';

export interface AdvancedSelectOption {
    value: string;
    label: string;
}

export interface AdvancedSearchField<T = any> {
    key: string;
    label: string;
    type: AdvancedFieldType;
    /** Solo per type 'select'. */
    options?: AdvancedSelectOption[];
    /** Estrae dall'elemento il valore su cui confrontare. */
    value: (item: T) => any;
}

export interface AdvancedSearchRule {
    id: number;
    field: string;
    operator: string;
    value: string;
    valueTo: string;
}

export interface AdvancedSearchState {
    match: AdvancedMatch;
    rules: AdvancedSearchRule[];
}

export interface AdvancedOperator {
    value: string;
    label: string;
    /** Quanti valori servono: 0 (es. "è vuoto"), 1, 2 (es. "tra"). */
    inputs: 0 | 1 | 2;
}

export const ADVANCED_OPERATORS: Record<AdvancedFieldType, AdvancedOperator[]> = {
    text: [
        {value: 'contains', label: 'contiene', inputs: 1},
        {value: 'notContains', label: 'non contiene', inputs: 1},
        {value: 'equals', label: 'è uguale a', inputs: 1},
        {value: 'notEquals', label: 'è diverso da', inputs: 1},
        {value: 'startsWith', label: 'inizia con', inputs: 1},
        {value: 'empty', label: 'è vuoto', inputs: 0},
        {value: 'notEmpty', label: 'non è vuoto', inputs: 0}
    ],
    number: [
        {value: 'eq', label: '=', inputs: 1},
        {value: 'neq', label: '≠', inputs: 1},
        {value: 'gt', label: '>', inputs: 1},
        {value: 'gte', label: '≥', inputs: 1},
        {value: 'lt', label: '<', inputs: 1},
        {value: 'lte', label: '≤', inputs: 1},
        {value: 'between', label: 'tra', inputs: 2},
        {value: 'empty', label: 'è vuoto', inputs: 0},
        {value: 'notEmpty', label: 'non è vuoto', inputs: 0}
    ],
    date: [
        {value: 'on', label: 'il giorno', inputs: 1},
        {value: 'from', label: 'dal', inputs: 1},
        {value: 'until', label: 'fino al', inputs: 1},
        {value: 'between', label: 'tra', inputs: 2},
        {value: 'empty', label: 'è vuota', inputs: 0},
        {value: 'notEmpty', label: 'non è vuota', inputs: 0}
    ],
    select: [
        {value: 'is', label: 'è', inputs: 1},
        {value: 'isNot', label: 'non è', inputs: 1},
        {value: 'empty', label: 'è vuoto', inputs: 0},
        {value: 'notEmpty', label: 'non è vuoto', inputs: 0}
    ]
};

let ruleIdSeed = 0;

export function nextRuleId(): number {
    ruleIdSeed += 1;
    return ruleIdSeed;
}

export function emptyAdvancedState(): AdvancedSearchState {
    return {match: 'and', rules: []};
}

export function createRule(field: AdvancedSearchField | undefined, partial: Partial<AdvancedSearchRule> = {}): AdvancedSearchRule {
    const type = field?.type ?? 'text';
    return {
        id: nextRuleId(),
        field: field?.key ?? '',
        operator: ADVANCED_OPERATORS[type][0].value,
        value: '',
        valueTo: '',
        ...partial
    };
}

export function cloneAdvancedState(state: AdvancedSearchState): AdvancedSearchState {
    return {
        match: state.match,
        rules: state.rules.map(rule => ({...rule}))
    };
}

/** Rigenera lo stato assegnando nuovi id e scartando le regole su campi inesistenti. */
export function normalizeAdvancedState(raw: any, fields: AdvancedSearchField[]): AdvancedSearchState {
    const state = emptyAdvancedState();
    if (!raw || typeof raw !== 'object') {
        return state;
    }
    state.match = raw.match === 'or' ? 'or' : 'and';
    if (!Array.isArray(raw.rules)) {
        return state;
    }
    raw.rules.forEach((candidate: any) => {
        const field = fields.find(f => f.key === candidate?.field);
        if (!field) {
            return;
        }
        const operator = ADVANCED_OPERATORS[field.type].find(op => op.value === candidate.operator);
        if (!operator) {
            return;
        }
        state.rules.push(createRule(field, {
            operator: operator.value,
            value: String(candidate.value ?? ''),
            valueTo: String(candidate.valueTo ?? '')
        }));
    });
    return state;
}

export function getOperator(field: AdvancedSearchField | undefined, operator: string): AdvancedOperator | undefined {
    return field ? ADVANCED_OPERATORS[field.type].find(op => op.value === operator) : undefined;
}

function isBlank(value: any): boolean {
    return value == null || (typeof value === 'string' && value.trim() === '');
}

/** Una regola con valori mancanti viene ignorata: aggiungere una riga vuota non svuota la tabella. */
export function isRuleActive(rule: AdvancedSearchRule, fields: AdvancedSearchField[]): boolean {
    const field = fields.find(f => f.key === rule.field);
    const operator = getOperator(field, rule.operator);
    if (!field || !operator) {
        return false;
    }
    if (operator.inputs === 0) {
        return true;
    }
    if (isBlank(rule.value)) {
        return false;
    }
    if (operator.inputs === 2 && isBlank(rule.valueTo)) {
        return false;
    }
    if (field.type === 'number') {
        return !isNaN(parseNumber(rule.value)) && (operator.inputs === 1 || !isNaN(parseNumber(rule.valueTo)));
    }
    if (field.type === 'date') {
        return parseDay(rule.value) !== null && (operator.inputs === 1 || parseDay(rule.valueTo) !== null);
    }
    return true;
}

export function activeRules(state: AdvancedSearchState, fields: AdvancedSearchField[]): AdvancedSearchRule[] {
    return state.rules.filter(rule => isRuleActive(rule, fields));
}

export function countActiveRules(state: AdvancedSearchState, fields: AdvancedSearchField[]): number {
    return activeRules(state, fields).length;
}

/** Firma stabile delle regole attive, usata per riconoscere se i filtri coincidono con un preset. */
export function advancedStateSignature(state: AdvancedSearchState, fields: AdvancedSearchField[]): string {
    const rules = activeRules(state, fields)
        .map(rule => [rule.field, rule.operator, rule.value.trim().toLowerCase(), rule.valueTo.trim().toLowerCase()].join(':'))
        .sort();
    if (rules.length === 0) {
        return '';
    }
    // Con una sola regola AND e OR sono equivalenti.
    const match = rules.length === 1 ? 'and' : state.match;
    return match + '|' + rules.join('|');
}

export function describeRule(rule: AdvancedSearchRule, fields: AdvancedSearchField[]): string {
    const field = fields.find(f => f.key === rule.field);
    const operator = getOperator(field, rule.operator);
    if (!field || !operator) {
        return '';
    }
    const format = (value: string): string => {
        if (field.type === 'select') {
            return field.options?.find(option => option.value === value)?.label ?? value;
        }
        if (field.type === 'date') {
            const day = parseDay(value);
            return day ? day.toLocaleDateString('it-IT') : value;
        }
        return value;
    };
    const parts = [field.label, operator.label];
    if (operator.inputs >= 1) {
        parts.push(field.type === 'text' ? `"${rule.value}"` : format(rule.value));
    }
    if (operator.inputs === 2) {
        parts.push('e', format(rule.valueTo));
    }
    return parts.join(' ');
}

export function applyAdvancedSearch<T>(items: Array<T>, fields: AdvancedSearchField<T>[], state: AdvancedSearchState | null | undefined): Array<T> {
    if (!items || !state) {
        return items;
    }
    const rules = activeRules(state, fields);
    if (rules.length === 0) {
        return items;
    }
    const resolved = rules.map(rule => ({
        field: fields.find(f => f.key === rule.field) as AdvancedSearchField<T>,
        rule
    }));
    const useAnd = state.match !== 'or';
    return items.filter(item => {
        const test = ({field, rule}: { field: AdvancedSearchField<T>; rule: AdvancedSearchRule }) =>
            evaluateRule(field.type, field.value(item), rule);
        return useAnd ? resolved.every(test) : resolved.some(test);
    });
}

function evaluateRule(type: AdvancedFieldType, raw: any, rule: AdvancedSearchRule): boolean {
    const operator = rule.operator;
    if (operator === 'empty') {
        return isBlank(raw);
    }
    if (operator === 'notEmpty') {
        return !isBlank(raw);
    }

    switch (type) {
        case 'text': {
            const text = isBlank(raw) ? '' : String(raw).toLowerCase().trim();
            const query = rule.value.toLowerCase().trim();
            switch (operator) {
                case 'contains': return text.includes(query);
                case 'notContains': return !text.includes(query);
                case 'equals': return text === query;
                case 'notEquals': return text !== query;
                case 'startsWith': return text.startsWith(query);
            }
            return false;
        }
        case 'number': {
            if (isBlank(raw)) {
                return operator === 'neq';
            }
            const n = Number(raw);
            const a = parseNumber(rule.value);
            const b = parseNumber(rule.valueTo);
            switch (operator) {
                case 'eq': return n === a;
                case 'neq': return n !== a;
                case 'gt': return n > a;
                case 'gte': return n >= a;
                case 'lt': return n < a;
                case 'lte': return n <= a;
                case 'between': return n >= Math.min(a, b) && n <= Math.max(a, b);
            }
            return false;
        }
        case 'date': {
            const date = isBlank(raw) ? null : new Date(raw);
            if (!date || isNaN(date.getTime())) {
                return false;
            }
            const time = date.getTime();
            const from = parseDay(rule.value);
            const to = parseDay(rule.valueTo);
            const startOf = (day: Date) => day.getTime();
            const endOf = (day: Date) => day.getTime() + 24 * 60 * 60 * 1000 - 1;
            switch (operator) {
                case 'on': return !!from && time >= startOf(from) && time <= endOf(from);
                case 'from': return !!from && time >= startOf(from);
                case 'until': return !!from && time <= endOf(from);
                case 'between': {
                    if (!from || !to) {
                        return false;
                    }
                    const first = from.getTime() <= to.getTime() ? from : to;
                    const last = first === from ? to : from;
                    return time >= startOf(first) && time <= endOf(last);
                }
            }
            return false;
        }
        case 'select': {
            const value = isBlank(raw) ? '' : String(raw);
            return operator === 'is' ? value === rule.value : value !== rule.value;
        }
    }
    return false;
}

function parseNumber(value: string): number {
    return Number(String(value ?? '').trim().replace(',', '.'));
}

/** Converte 'YYYY-MM-DD' in mezzanotte locale (evita gli slittamenti di fuso di new Date('YYYY-MM-DD')). */
function parseDay(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim());
    if (!match) {
        return null;
    }
    const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return isNaN(day.getTime()) ? null : day;
}
